import { uniqBy } from 'lodash';
import { EnvelopeStatusLabels } from '@/views/Documents/views/SentEvelopesSection/enums';
import type {
  CoreInteractionType,
  InteractionTypeFilter,
} from '@/views/Interactions/state/filters';

type Status = {
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
};

const sortStatuses = (status: Status[]) =>
  status.sort((obj1, obj2) => obj1.label.localeCompare(obj2.label));

const listingStatus: Status[] = sortStatuses([
  { label: 'Active', value: 'active' },
  { label: 'Active Contingent', value: 'activeCont' },
  { label: 'Back Up', value: 'backUp' },
  {
    label: 'Closed',
    value: 'closed',
    disabled: true,
    tooltip: 'This status is set automatically when the transaction closes',
  },
  { label: 'Coming Soon', value: 'comingSoon' },
  { label: 'Expired', value: 'expired' },
  { label: 'Lost', value: 'lost' },
  { label: 'Off Market', value: 'offMarket' },
  { label: 'On Market', value: 'onMarket' },
  {
    label: 'Pending',
    value: 'pending',
    disabled: true,
    tooltip: 'This status is set automatically when an offer is accepted',
  },
  { label: 'Pre-Listing', value: 'preListing' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Withdrawn', value: 'withdrawn' },
]);

const offerStatus: Status[] = sortStatuses([
  { label: 'Active', value: 'active' },
  { label: 'Active Contingent', value: 'activeCont' },
  { label: 'Back Up', value: 'backUp' },
  { label: 'Canceled', value: 'cancelled' },
  {
    label: 'Closed',
    value: 'closed',
    disabled: true,
    tooltip: 'This status is set automatically when the transaction closes',
  },
  {
    label: 'Pending',
    value: 'pending',
    disabled: true,
    tooltip: 'This status is set automatically when an offer is accepted',
  },
  { label: 'Ratified', value: 'ratified' },
  { label: 'Rejected', value: 'rejected' },
]);

const landlordLeaseStatus: Status[] = sortStatuses([
  { label: 'Active', value: 'active' },
  {
    label: 'Closed',
    value: 'closed',
    disabled: true,
    tooltip: 'This status is set automatically when the transaction closes',
  },
  { label: 'Coming Soon', value: 'comingSoon' },
  { label: 'Expired', value: 'expired' },
  { label: 'Lost', value: 'lost' },
  { label: 'On Market', value: 'onMarket' },
  {
    label: 'Pending',
    value: 'pending',
    disabled: true,
    tooltip: 'This status is set automatically when an offer is accepted',
  },
  { label: 'Pre-Listing', value: 'preListing' },
  { label: 'Withdrawn', value: 'withdrawn' },
]);

const tenantLeaseStatus: Status[] = sortStatuses([
  { label: 'Active', value: 'active' },
  { label: 'Active Contingent', value: 'activeCont' },
  { label: 'Back Up', value: 'backUp' },
  { label: 'Canceled', value: 'cancelled' },
  {
    label: 'Closed',
    value: 'closed',
    disabled: true,
    tooltip: 'This status is set automatically when the transaction closes',
  },
  {
    label: 'Pending',
    value: 'pending',
    disabled: true,
    tooltip: 'This status is set automatically when an offer is accepted',
  },
  { label: 'Ratified', value: 'ratified' },
  { label: 'Rejected', value: 'rejected' },
]);

const allLeaseStatus: Status[] = sortStatuses(
  uniqBy([...landlordLeaseStatus, ...tenantLeaseStatus], 'value'),
);

const clientGroupStatus: Status[] = sortStatuses(
  Object.entries(EnvelopeStatusLabels).map(([value, label]) => ({
    value,
    label,
  })),
);

const allStatuses: Status[] = sortStatuses(
  uniqBy([...listingStatus, ...offerStatus, ...allLeaseStatus], 'value'),
);

const referralStatus: Status[] = sortStatuses(
  uniqBy(
    [{ label: 'Not Added Yet', value: 'notAvailable' }, ...allStatuses],
    'value',
  ),
);

export const getTransactionStatusList = (
  type?: string,
  representation?: string | null,
) => {
  if (type === 'listing') return listingStatus;
  if (type === 'offer') return offerStatus;

  if (type === 'lease') {
    if (representation === 'landlord') {
      return landlordLeaseStatus;
    }
    if (representation === 'tenant') {
      return tenantLeaseStatus;
    }
    return allLeaseStatus;
  }

  if (type === 'clientGroup') return clientGroupStatus;
  if (type === 'referral') return referralStatus;

  return [];
};

const transactionStatusKeys: Partial<Record<CoreInteractionType, string>> = {
  clientGroup: 'agreementStatus',
  referral: 'referralInteractionStatus',
};

export const getTransactionStatusListForFilters = (
  type: InteractionTypeFilter,
) => {
  const key = transactionStatusKeys[type] ?? 'status';

  // Ignore representation for the Filters. Always show all Lease statuses.
  return getTransactionStatusList(type, null).map(({ label, value }) => ({
    label,
    value: `${key}:${value}`,
  }));
};
